import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Youtube, ArrowUpRight, Shield, Award, Clock } from 'lucide-react';

const Footer = ({ onOpenCalendar }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer style={{ background: 'var(--brand-dark)' }}>
      {/* Pre-footer CTA */}
      <div className="container">
        <div 
          className="relative -top-16 rounded-3xl p-8 md:p-12 text-center overflow-hidden"
          style={{ background: 'var(--brand-primary)' }}
        >
          <div className="relative z-10">
            <h3 className="heading-1 mb-4" style={{ color: 'var(--brand-dark)' }}>
              Start Your Transformation Today
            </h3>
            <p className="body-large mb-6 max-w-2xl mx-auto" style={{ color: 'var(--brand-hover)' }}>
              Join ambitious Indian SMEs who've switched to smarter business systems
            </p>
            <button
              onClick={onOpenCalendar}
              className="btn-primary"
            >
              Book Your Free 7-Min Demo
              <ArrowUpRight size={18} />
            </button>
          </div>
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20" style={{ background: 'var(--brand-dark)' }} />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-20" style={{ background: 'var(--brand-dark)' }} />
        </div>
      </div>

      {/* Main Footer */}
      <div className="container pb-8 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <img 
                src="/logo.png" 
                alt="Zenova Growth Partners" 
                className="h-10 w-10 rounded-full object-cover"
                style={{ background: 'rgba(255,255,255,0.1)' }}
              />
              <span className="text-white font-bold text-xl">Zenova Growth Partners</span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              We help SME owners transform chaos-driven businesses into system-run, future-ready organizations.
            </p>
            <div className="flex gap-3">
              {[Linkedin, Twitter, Youtube].map((Icon, i) => (
                <a 
                  key={i}
                  href="#" 
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{ background: 'rgba(255,255,255,0.1)' }}
                >
                  <Icon size={18} className="text-white/70" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-base mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {['Services', 'STRENGTH Framework', 'ROI Calculator', 'Testimonials', 'FAQ'].map((item) => (
                <li key={item}>
                  <a 
                    href={`/#${item.toLowerCase().replace(' ', '-')}`}
                    className="text-white/60 hover:text-white text-sm transition-colors duration-200 flex items-center gap-1 group"
                    style={{ textDecoration: 'none' }}
                  >
                    {item}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-base mb-5">Services</h4>
            <ul className="space-y-3">
              {['Business System Audit', 'Operations & SOP', 'People & Accountability', 'Cash Flow Systems', 'Dashboard & KPIs'].map((item) => (
                <li key={item}>
                  <a 
                    href="#services"
                    className="text-white/60 hover:text-white text-sm transition-colors duration-200"
                    style={{ textDecoration: 'none' }}
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-base mb-5">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@zenovagrowthpartners.com" 
                  className="text-white/60 hover:text-white text-sm transition-colors duration-200 flex items-center gap-3"
                  style={{ textDecoration: 'none' }}
                >
                  <Mail size={16} className="text-white/40 flex-shrink-0" />
                  info@zenovagrowthpartners.com
                </a>
              </li>
              <li>
                <a 
                  href="tel:+919876543210" 
                  className="text-white/60 hover:text-white text-sm transition-colors duration-200 flex items-center gap-3"
                  style={{ textDecoration: 'none' }}
                >
                  <Phone size={16} className="text-white/40 flex-shrink-0" />
                  +91 9899668555
                </a>
              </li>
              <li>
                <span className="text-white/60 text-sm flex items-start gap-3">
                  <MapPin size={16} className="text-white/40 flex-shrink-0 mt-0.5" />
                  209, 1st Floor, New Gandhi Nagar, Ghaziabad, 201001
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-8">
            {[
              { icon: Shield, text: "ISO 27001 Certified" },
              { icon: Award, text: "GST Compliant" },
              { icon: Clock, text: "99.9% Uptime" }
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-white/50 text-sm">
                <Icon size={16} />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs">
              © {currentYear} Zenova Growth Partners. All rights reserved.
            </p>
            <div className="flex gap-6">
              {['Privacy Policy', 'Terms of Service', 'Refund Policy'].map((item) => (
                <a 
                  key={item}
                  href="#" 
                  className="text-white/40 hover:text-white/70 text-xs transition-colors" 
                  style={{ textDecoration: 'none' }}
                >
                  {item}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
